import { useState } from "react";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { LoadState, Panel, Pill } from "@/components/admin/AdminUI";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import useAdminLoad from "@/hooks/useAdminLoad";
import { api } from "@/lib/api";

function SwitchRow({ item, canEdit, onAsk }) {
  return (
    <li className="flex items-center justify-between gap-4 py-3" data-testid={`kill-row-${item.category}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium">{item.label}</p>
        <p className="cw-muted font-mono text-xs">{item.category}</p>
      </div>
      <div className="flex items-center gap-3">
        <Pill tone={item.killed ? "warning" : "success"} data-testid={`kill-state-${item.category}`}>{item.killed ? "Stopped" : "Sending"}</Pill>
        <Switch checked={!item.killed} disabled={!canEdit} onCheckedChange={(on) => onAsk({ ...item, killed: !on })}
          aria-label={`${item.label} pushes`} data-testid={`kill-switch-${item.category}`} />
      </div>
    </li>
  );
}

export default function KillSwitches({ canEdit }) {
  const { data, error, loading, reload, setData } = useAdminLoad("/admin/push/kill-switches");
  const [pending, setPending] = useState(null);
  const [reason, setReason] = useState("");

  const apply = async () => {
    if (reason.trim().length < 3) throw new Error("Add a short reason (at least 3 characters).");
    const res = await api("/admin/push/kill-switches", { method: "PUT", body: { category: pending.category, killed: pending.killed, reason: reason.trim() } });
    setData(res);
    toast.success(`${pending.label}: ${pending.killed ? "stopped for everyone" : "sending again"}.`);
  };

  return (
    <Panel className="p-6" data-testid="kill-switches-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold">Kill switches</h2>
          <p className="cw-soft mt-1 max-w-md text-sm leading-6">Stop a whole category for everyone, right away. People’s own settings are kept.</p>
        </div>
        {!canEdit && <Pill tone="neutral" data-testid="kill-switches-readonly">View only</Pill>}
      </div>
      <div className="mt-4">
        <LoadState loading={loading} error={error} onRetry={reload}>
          <ul className="divide-y divide-border" data-testid="kill-switch-list">
            {data?.items?.map((it) => <SwitchRow key={it.category} item={it} canEdit={canEdit} onAsk={(p) => { setReason(""); setPending(p); }} />)}
          </ul>
        </LoadState>
      </div>
      <ConfirmDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)} onConfirm={apply} destructive={!!pending?.killed}
        testId="kill-switch-confirm-dialog" confirmDisabled={reason.trim().length < 3} confirmLabel={pending?.killed ? "Stop sending" : "Resume sending"}
        title={pending?.killed ? `Stop all “${pending?.label}” pushes?` : `Resume “${pending?.label}” pushes?`}
        description={pending?.killed ? "Nobody gets this kind of push until it’s turned back on. Each attempt is logged as Kill switch." : "This kind of push goes out again, following the usual rules."}>
        <div><Label htmlFor="kill-reason">Reason (saved to the admin log)</Label>
          <Textarea id="kill-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="mt-2" data-testid="kill-switch-reason-input" /></div>
      </ConfirmDialog>
    </Panel>
  );
}
